import { useRef, useState, type ReactNode } from 'react';
import {
  ChevronRight,
  Download,
  Folder,
  FolderInput,
  Trash2,
  Upload,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ConfirmationDialog } from '@/components/ui/ConfirmationDialog';
import { DataLoader } from '@/components/ui/DataLoader';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useDeleteDocument,
  useDownloadDocument,
  useEntityFolder,
  useInvestorDocumentFolders,
  useMoveInvestorDocument,
  useUpdateDocumentInvestorVisibility,
  useUploadInvestorDocuments,
} from '@/hooks/react-query/useDocuments';
import {
  EntityType,
  type FundDocumentContextType,
  type InvestorDocumentFoldersResponse,
  type InvestorFolderDocument,
  type InvestorFundFolder,
} from '@/types/document';
import { formatDate } from '@/utils/formatters';
import { InvestorVisibilityUploadDialog } from './browser/InvestorVisibilityUploadDialog';
import { getInvestorVisibilityLabels } from './investorVisibilityLabels';
import {
  FUND_DOCUMENT_FOLDERS,
  GENERAL_FOLDER_NAME,
  fundDocumentFolderName,
} from './fundDocumentFolders';

/** contextType null inside a fund is the fund folder itself. */
type FolderLocation =
  | { kind: 'ROOT' }
  | { kind: 'GENERAL' }
  | {
      kind: 'FUND';
      fundId: string;
      contextType: FundDocumentContextType | null;
    };

interface InvestorDocumentFoldersProps {
  investorId: string;
  investorName: string;
}

export function InvestorDocumentFolders({
  investorId,
  investorName,
}: InvestorDocumentFoldersProps) {
  const foldersQuery = useInvestorDocumentFolders(investorId);
  const [location, setLocation] = useState<FolderLocation>({ kind: 'ROOT' });

  return (
    <DataLoader query={foldersQuery}>
      {(folders: InvestorDocumentFoldersResponse) => (
        <div className="space-y-4">
          <Breadcrumb
            investorName={investorName}
            funds={folders.funds}
            location={location}
            onNavigate={setLocation}
          />
          <FolderView
            investorId={investorId}
            folders={folders}
            location={location}
            onNavigate={setLocation}
          />
        </div>
      )}
    </DataLoader>
  );
}

interface BreadcrumbProps {
  investorName: string;
  funds: InvestorFundFolder[];
  location: FolderLocation;
  onNavigate: (location: FolderLocation) => void;
}

function Breadcrumb({
  investorName,
  funds,
  location,
  onNavigate,
}: BreadcrumbProps) {
  const crumbs: { label: string; target: FolderLocation }[] = [
    { label: investorName, target: { kind: 'ROOT' } },
  ];
  if (location.kind === 'GENERAL') {
    crumbs.push({ label: GENERAL_FOLDER_NAME, target: location });
  }
  if (location.kind === 'FUND') {
    crumbs.push({
      label: fundName(funds, location.fundId),
      target: { kind: 'FUND', fundId: location.fundId, contextType: null },
    });
    if (location.contextType) {
      crumbs.push({
        label: fundDocumentFolderName(location.contextType),
        target: location,
      });
    }
  }

  return (
    <nav className="flex items-center gap-1 text-sm" aria-label="Folder path">
      {crumbs.map((crumb, index) => {
        const isCurrent = index === crumbs.length - 1;
        return (
          <span key={crumb.label} className="flex items-center gap-1">
            {index > 0 && (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )}
            {isCurrent ? (
              <span className="font-medium">{crumb.label}</span>
            ) : (
              <button
                type="button"
                onClick={() => onNavigate(crumb.target)}
                className="text-muted-foreground hover:text-foreground hover:underline"
              >
                {crumb.label}
              </button>
            )}
          </span>
        );
      })}
    </nav>
  );
}

interface FolderViewProps {
  investorId: string;
  folders: InvestorDocumentFoldersResponse;
  location: FolderLocation;
  onNavigate: (location: FolderLocation) => void;
}

function FolderView({
  investorId,
  folders,
  location,
  onNavigate,
}: FolderViewProps) {
  if (location.kind === 'ROOT') {
    const generalCount = folders.documents.filter(isInGeneral).length;
    return (
      <FolderList>
        <FolderRow
          name={GENERAL_FOLDER_NAME}
          count={generalCount}
          onOpen={() => onNavigate({ kind: 'GENERAL' })}
        />
        {folders.funds.map((fund) => (
          <FolderRow
            key={fund.fund_id}
            name={fund.fund_name}
            count={
              folders.documents.filter(
                (entry) => entry.fund_id === fund.fund_id
              ).length
            }
            onOpen={() =>
              onNavigate({
                kind: 'FUND',
                fundId: fund.fund_id,
                contextType: null,
              })
            }
          />
        ))}
      </FolderList>
    );
  }

  const fundId = location.kind === 'FUND' ? location.fundId : null;
  const contextType = location.kind === 'FUND' ? location.contextType : null;
  const documents = folders.documents.filter(
    (entry) => entry.fund_id === fundId && entry.context_type === contextType
  );
  const isFundFolder = location.kind === 'FUND' && contextType === null;

  return (
    <div className="space-y-4">
      {isFundFolder && fundId && (
        <FolderList>
          {FUND_DOCUMENT_FOLDERS.map((subfolder) => (
            <FolderRow
              key={subfolder.contextType}
              name={subfolder.name}
              count={
                folders.documents.filter(
                  (entry) =>
                    entry.fund_id === fundId &&
                    entry.context_type === subfolder.contextType
                ).length
              }
              onOpen={() =>
                onNavigate({
                  kind: 'FUND',
                  fundId,
                  contextType: subfolder.contextType,
                })
              }
            />
          ))}
        </FolderList>
      )}
      <UploadBar
        investorId={investorId}
        fundId={fundId}
        contextType={contextType}
      />
      <DocumentTable
        investorId={investorId}
        funds={folders.funds}
        documents={documents}
      />
    </div>
  );
}

function FolderList({ children }: { children: ReactNode }) {
  return <div className="divide-y rounded-md border">{children}</div>;
}

interface FolderRowProps {
  name: string;
  count: number;
  onOpen: () => void;
}

function FolderRow({ name, count, onOpen }: FolderRowProps) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-muted/50"
    >
      <Folder className="h-4 w-4 text-muted-foreground" />
      <span className="font-medium">{name}</span>
      <span className="ml-auto text-sm text-muted-foreground">
        {count} {count === 1 ? 'file' : 'files'}
      </span>
    </button>
  );
}

interface UploadBarProps {
  investorId: string;
  fundId: string | null;
  contextType: FundDocumentContextType | null;
}

function UploadBar({ investorId, fundId, contextType }: UploadBarProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [portalVisible, setPortalVisible] = useState(false);
  const { data: investorFolder } = useEntityFolder(
    EntityType.INVESTOR,
    investorId
  );
  const upload = useUploadInvestorDocuments();
  const visibilityLabels = getInvestorVisibilityLabels(EntityType.INVESTOR);

  const addFiles = (fileList: FileList | null) => {
    if (!fileList?.length) return;
    // Copy now: the list is live and clearing the input's value empties it.
    const added = Array.from(fileList);
    const addedNames = new Set(added.map((file) => file.name));
    setPendingFiles((previous) => [
      ...previous.filter((file) => !addedNames.has(file.name)),
      ...added,
    ]);
    setShowUploadDialog(true);
  };

  const closeDialog = () => {
    setShowUploadDialog(false);
    setPendingFiles([]);
    setPortalVisible(false);
  };

  const uploadPendingFiles = () => {
    if (!investorFolder) return;
    upload.mutate(
      {
        investorId,
        files: pendingFiles,
        collectionId: investorFolder.folder_id,
        portalVisible,
        fundId,
        contextType,
      },
      { onSuccess: closeDialog }
    );
  };

  return (
    <div className="flex flex-wrap items-center gap-4">
      <input
        ref={inputRef}
        id="investor-document-upload"
        type="file"
        multiple
        className="sr-only"
        onChange={(event) => {
          addFiles(event.target.files);
          // Allow re-selecting the same file after removing it.
          event.target.value = '';
        }}
      />
      <Button
        variant="outline"
        size="sm"
        disabled={!investorFolder}
        onClick={() => inputRef.current?.click()}
      >
        <Upload className="mr-2 h-4 w-4" />
        Upload files
      </Button>
      <InvestorVisibilityUploadDialog
        open={showUploadDialog}
        onOpenChange={(open) => {
          if (!open) closeDialog();
        }}
        files={pendingFiles}
        visibilityLabel={visibilityLabels.toggleLabel}
        visibilityDescription={visibilityLabels.toggleDescription}
        investorVisible={portalVisible}
        onInvestorVisibleChange={setPortalVisible}
        onAddFiles={addFiles}
        onRemoveFile={(fileName) =>
          setPendingFiles((previous) =>
            previous.filter((file) => file.name !== fileName)
          )
        }
        onUpload={uploadPendingFiles}
        isUploading={upload.isPending}
      />
    </div>
  );
}

interface DocumentTableProps {
  investorId: string;
  funds: InvestorFundFolder[];
  documents: InvestorFolderDocument[];
}

function DocumentTable({ investorId, funds, documents }: DocumentTableProps) {
  const updateVisibility = useUpdateDocumentInvestorVisibility();
  const downloadDocument = useDownloadDocument();
  const deleteDocument = useDeleteDocument();
  const [documentIdToDelete, setDocumentIdToDelete] = useState<string | null>(
    null
  );

  if (documents.length === 0) {
    return (
      <p className="py-6 text-center text-sm text-muted-foreground">
        No files in this folder
      </p>
    );
  }

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Uploaded</TableHead>
            <TableHead>Name</TableHead>
            <TableHead>Investor portal</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {documents.map(({ document }) => (
            <TableRow key={document.id}>
              <TableCell className="whitespace-nowrap">
                {formatDate(document.upload_date)}
              </TableCell>
              <TableCell>{document.name}</TableCell>
              <TableCell>
                <Switch
                  aria-label={`Show ${document.name} in the investor portal`}
                  checked={document.portal_visible ?? false}
                  onCheckedChange={(visible) =>
                    updateVisibility.mutate({
                      documentId: document.id,
                      entityType: EntityType.INVESTOR,
                      entityId: investorId,
                      investorVisible: visible,
                    })
                  }
                />
              </TableCell>
              <TableCell>
                <div className="flex justify-end gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Download ${document.name}`}
                    onClick={() =>
                      downloadDocument.mutate({
                        id: document.id,
                        documentName: document.original_filename,
                      })
                    }
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                  {isInvestorUpload(document.entity_associations) && (
                    <>
                      <MoveMenu
                        investorId={investorId}
                        documentId={document.id}
                        documentName={document.name}
                        funds={funds}
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={`Delete ${document.name}`}
                        onClick={() => setDocumentIdToDelete(document.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <ConfirmationDialog
        open={documentIdToDelete !== null}
        onOpenChange={(open) => !open && setDocumentIdToDelete(null)}
        title="Delete file"
        description="The file is removed for the investor too. This cannot be undone."
        confirmLabel="Delete"
        variant="destructive"
        isLoading={deleteDocument.isPending}
        onConfirm={() => {
          if (!documentIdToDelete) return;
          deleteDocument.mutate(documentIdToDelete, {
            onSuccess: () => setDocumentIdToDelete(null),
          });
        }}
      />
    </>
  );
}

interface MoveMenuProps {
  investorId: string;
  documentId: string;
  documentName: string;
  funds: InvestorFundFolder[];
}

function MoveMenu({
  investorId,
  documentId,
  documentName,
  funds,
}: MoveMenuProps) {
  const moveDocument = useMoveInvestorDocument();
  const moveTo = (
    fundId: string | null,
    contextType: FundDocumentContextType | null
  ) => moveDocument.mutate({ investorId, documentId, fundId, contextType });

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={`Move ${documentName}`}
          disabled={moveDocument.isPending}
        >
          <FolderInput className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => moveTo(null, null)}>
          {GENERAL_FOLDER_NAME}
        </DropdownMenuItem>
        {funds.map((fund) => (
          <div key={fund.fund_id}>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>{fund.fund_name}</DropdownMenuLabel>
            <DropdownMenuItem onClick={() => moveTo(fund.fund_id, null)}>
              Fund folder
            </DropdownMenuItem>
            {FUND_DOCUMENT_FOLDERS.map((subfolder) => (
              <DropdownMenuItem
                key={subfolder.contextType}
                onClick={() => moveTo(fund.fund_id, subfolder.contextType)}
              >
                {subfolder.name}
              </DropdownMenuItem>
            ))}
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function isInGeneral(entry: InvestorFolderDocument): boolean {
  return entry.fund_id === null;
}

/** Generated notices reach the investor through a notification and are
 *  filed by the system, so only uploads can be moved or deleted. */
function isInvestorUpload(
  associations: InvestorFolderDocument['document']['entity_associations']
): boolean {
  return (associations ?? []).some(
    (association) => association.entity_type === EntityType.INVESTOR
  );
}

function fundName(funds: InvestorFundFolder[], fundId: string): string {
  return funds.find((fund) => fund.fund_id === fundId)?.fund_name ?? 'Fund';
}
